// Phase 9 /models/registry — Phase 1 aero + sensor model catalogue.
//
// Dark card grid. Each card carries: name, label pill, version,
// calibration status, envelope summary, and a "View card" button that
// opens a modal rendering the model_card_md payload as plain text.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Cpu, X } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { models as modelsApi } from "@/api/endpoints";
import { formatApiError } from "@/lib/formatApiError";

function labelClass(l) {
    switch ((l || "").toLowerCase()) {
        case "validated":
            return "bg-ux-success/20 text-ux-success border-ux-success/40";
        case "provisional":
            return "bg-ux-warning-soft text-ux-warning border-ux-warning/40";
        case "experimental":
            return "bg-ux-bg-2 text-ux-text-3 border-ux-border-2";
        default:
            return "bg-ux-bg-2 text-ux-text-3 border-ux-border-2";
    }
}

export default function ModelsRegistryPage() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [open, setOpen] = useState(null); // { model, card }

    useEffect(() => {
        (async () => {
            try {
                const data = await modelsApi.list();
                setRows(Array.isArray(data) ? data : data?.items || []);
            } catch (e) {
                setErr(formatApiError(e, "Failed to load models"));
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const viewCard = async (m) => {
        try {
            const card = await modelsApi.getCard(m.id || m.model_id);
            setOpen({ model: m, card });
        } catch (e) {
            toast.error(formatApiError(e, "Card unavailable"));
        }
    };

    return (
        <div data-testid="models-registry-page">
            <PageHeader
                title="Model Registry"
                subtitle="Aero + sensor models catalogue — validity envelopes, calibration status, and model cards."
            />

            {err && (
                <div className="mb-4 p-3 rounded-md border border-ux-danger/40 bg-ux-danger/10 text-ux-danger text-[13px]">
                    {err}
                </div>
            )}
            {loading && (
                <div className="text-ux-text-3 text-[13px]" data-testid="models-loading">
                    loading…
                </div>
            )}

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {rows.map((m) => {
                    const id = m.id || m.model_id;
                    const env = m.validity_envelope || m.envelope || {};
                    return (
                        <div
                            key={id}
                            className="rounded-lg border border-ux-border-1 bg-ux-bg-1 p-4 flex flex-col gap-2 hover:border-ux-accent/40 transition-colors"
                            data-testid={`models-card-${id}`}
                        >
                            <div className="flex items-start gap-2">
                                <Cpu className="w-4 h-4 text-ux-text-3 mt-0.5 shrink-0" />
                                <div className="min-w-0 flex-1">
                                    <div className="text-[14px] text-ux-text-1 font-medium truncate">
                                        {m.name || id}
                                    </div>
                                    <div className="text-[11px] font-mono text-ux-text-3 truncate">
                                        {m.kind || "model"} · v{m.version || "?"}
                                    </div>
                                </div>
                                <span
                                    className={[
                                        "text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border",
                                        labelClass(m.label),
                                    ].join(" ")}
                                    data-testid={`models-label-${id}`}
                                >
                                    {m.label || "—"}
                                </span>
                            </div>
                            <dl className="text-[11px] grid grid-cols-2 gap-x-3 gap-y-1 text-ux-text-2 mt-1">
                                <dt className="text-ux-text-3">calibration</dt>
                                <dd className="truncate">{m.calibration_status || "—"}</dd>
                                <dt className="text-ux-text-3">mass</dt>
                                <dd className="truncate">
                                    {env?.mass_kg
                                        ? `${env.mass_kg.min ?? "?"}–${env.mass_kg.max ?? "?"} kg`
                                        : "—"}
                                </dd>
                                <dt className="text-ux-text-3">speed</dt>
                                <dd className="truncate">
                                    {env?.speed_mps
                                        ? `${env.speed_mps.min ?? "?"}–${env.speed_mps.max ?? "?"} m/s`
                                        : "—"}
                                </dd>
                            </dl>
                            <button
                                onClick={() => viewCard(m)}
                                className="mt-2 self-start inline-flex items-center gap-1 text-[12px] text-ux-accent hover:underline"
                                data-testid={`models-view-card-${id}`}
                            >
                                View card →
                            </button>
                        </div>
                    );
                })}
                {!loading && rows.length === 0 && (
                    <div className="col-span-full text-[13px] text-ux-text-3">
                        No models registered yet.
                    </div>
                )}
            </div>

            {open && (
                <div
                    className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) setOpen(null);
                    }}
                >
                    <div
                        className="w-full max-w-3xl bg-ux-bg-1 rounded-lg border border-ux-border-1 max-h-[85vh] flex flex-col"
                        data-testid="models-card-modal"
                    >
                        <div className="flex items-center justify-between px-4 py-3 border-b border-ux-border-1">
                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-ux-text-3">
                                    Model card
                                </div>
                                <div className="text-[15px] text-ux-text-1 font-medium">
                                    {open.model.name || open.model.id}
                                </div>
                            </div>
                            <button
                                onClick={() => setOpen(null)}
                                className="text-ux-text-3 hover:text-ux-text-1"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <pre
                            className="flex-1 overflow-auto p-4 text-[12px] text-ux-text-2 font-mono whitespace-pre-wrap"
                            data-testid="models-card-modal-body"
                        >
                            {open.card?.model_card_md || open.card?.card || JSON.stringify(open.card, null, 2)}
                        </pre>
                    </div>
                </div>
            )}
        </div>
    );
}
